import { useMutation, useQuery } from "react-query";
import { showToast } from "@wix/dashboard-sdk";
import { wixApi } from "../../services/axios/api/wixApi";
import { wixProductParams, wixStoreDetailsParam } from "../../services/models/types/wixProduct.types";
import { useEffect, useState } from "react";
import WixProduct from "../../models/wix-product";

/**
 * The `useWixProducts` function is a custom hook that fetches products from the Wix API based on the
 * provided parameters.
 * @returns The function `useWixProducts` is returning the result of the `useQuery` hook.
 */
export function useWixProducts({
  page = 1,
  visibility,
  keywords,
  collectionId,
  withVariants = 1,
  withCollections = 1,
  exportStatus = "",
}: wixProductParams) {
  return useQuery(
    ["wix-products", page, visibility, keywords, collectionId, withVariants, withCollections, exportStatus],
    () =>
      wixApi.fetchProducts({ page, visibility, keywords, collectionId, withVariants, withCollections, exportStatus }),
    {
      retry: 0,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );
}

/**
 * The function `useWixCollectionProducts` is a custom hook that fetches collection products from the
 * Wix API and returns the result using the `useQuery` hook.
 */
export function useWixCollectionProducts({ collectionId }: any) {
  return useQuery(["wix-products", collectionId], () => wixApi.fetchCollectionProducts(collectionId), {
    retry: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });
}

/**
 * The function `useWixCollections` is a TypeScript function that uses the `useQuery` hook to fetch a
 * collection from the Wix API based on the provided name.
 */
export function useWixCollections({ name, page = 1 }: any) {
  return useQuery(["wix-collections", name, page], () => wixApi.fetchCollection(name, page), {
    retry: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * The `useWixProductsImport` function is a custom hook that uses the `useMutation` hook from a Wix API
 * to import products, and displays a success or error toast message accordingly.
 * @returns The function `useWixProductsImport` is returning the result of calling the `useMutation`
 * hook with the `wixApi.importProducts` function as the first argument. The `useMutation` hook returns
 * an object with properties and methods related to the mutation, such as `mutate`, `isLoading`,
 * `isError`, etc.
 */
export function useWixProductsImport() {
  return useMutation(wixApi.importProducts, {
    onSuccess: () => {
      showToast({
        type: "success",
        message: "Your Wix products are being imported.",
      });
    },
    onError: () => {
      showToast({
        type: "error",
        message: "Failed to import products. Please try again or contact support.",
      });
    },
  });
}

/**
 * The function `useWixCollectionsImport` is a TypeScript function that returns a mutation hook for
 * importing Wix collections, with success and error callbacks for displaying toast messages.
 * @returns The function `useWixCollectionsImport` is returning the result of calling the `useMutation`
 * hook with `wixApi.importCollections` as the first argument and an object as the second argument.
 */
export function useWixCollectionsImport() {
  return useMutation(wixApi.importCollections, {
    onSuccess: () => {
      showToast({
        type: "success",
        message: "Your Wix collections are being imported.",
      });
    },
    onError: () => {
      showToast({
        type: "error",
        message: "Failed to import collections. Please try again or contact support.",
      });
    },
  });
}

/**
 * The function `useWixProductsCount` is a custom hook that uses the `useQuery` hook from a library to
 * fetch the count of products from the Wix API and provides options for retrying, refetching, and
 * keeping previous data.
 */
export function useWixProductsCount() {
  return useQuery(["wix-products-count"], () => wixApi.fetchProductsCount(), {
    retry: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });
}

/**
 * The `useWixProductsDelete` function is a custom hook that handles the deletion of a product from
 * Wix, updating the exported and not exported data, and refetching the Faire products query.
 */
export function useWixProductsDelete({ setExportedData, setNotExportedData, faireProductsQuery }: any) {
  const [productIdToBeDeletedFromWix, setProductIdToBeDeletedFromWix] = useState("");
  const [showDeleteFromWixConfirmation, setShowDeleteFromWixConfirmation] = useState(false);

  useEffect(() => {
    if (productIdToBeDeletedFromWix !== "") {
      setShowDeleteFromWixConfirmation(true);
    }
  }, [productIdToBeDeletedFromWix]);

  const deleteProductMutation = useMutation(() => wixApi.deleteProductFromWix(productIdToBeDeletedFromWix), {
    onSuccess: (response: any) => {
      showToast({
        type: "success",
        message: response.message,
      });
      setExportedData((prevData: any) => prevData - 1);
      setNotExportedData((previousData: any) => previousData + 1);
      faireProductsQuery.refetch();
    },
    onError: (error: any) => {
      showToast({
        type: "error",
        message: error.message,
      });
    },
    onSettled: () => {
      setProductIdToBeDeletedFromWix("");
    },
  });

  const deleteFromWix = () => {
    deleteProductMutation.mutate();
  };

  return {
    deleteFromWix,
    productIdToBeDeletedFromWix,
    setProductIdToBeDeletedFromWix,
    showDeleteFromWixConfirmation,
    setShowDeleteFromWixConfirmation,
  };
}

/**
 * The `useWixStoreDetails` function is a custom hook that fetches Wix store details and returns the
 * data and loading state.
 * @param {wixStoreDetailsParam} initialData - The initialData parameter is the initial value for the
 * wixData state.
 * @returns The function `useWixStoreDetails` returns an object with two properties: `wixData` and
 * `isLoading`.
 */
export function useWixStoreDetails(initialData: wixStoreDetailsParam) {
  const [wixData, setWixData] = useState<wixStoreDetailsParam>(initialData);
  const [isLoading, setLoading] = useState(false);

  const fetchWixData = () => {
    if (isLoading) return;
    setLoading(true);

    WixProduct.getWixStoreDetails()
      .then((response: any) => {
        setWixData(response?.data);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchWixData();
  }, []);

  return { wixData, isLoading };
}
